import _ from "lodash";
import {
  Bar,
  BarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { IDateRange } from "./DateRangeSelector";
import { addDays, addWeeks, format, isWithinInterval } from "date-fns";
import { addHours } from "date-fns";

/*
Label props:
{
  index,
  cornerRadius, innerRadius, maxRadius, middleRadius
  cx, cy, fill,
  percent,
  startAngle,
  textAnchor,
  tooltipPosition,
  value,
  x, y
}
 */

const hlColor = "#003f5c";

interface ITimelineChartConfig {
  testNumDays: (days: number) => boolean;
  titleFn: (startDate: Date, numDays: number) => string;
  groupingFn: (startDate: Date, numDays: number, rawData: any[]) => any[];
}

const configOptions: ITimelineChartConfig[] = [
  {
    testNumDays: (days: number) => days === 1, // For just one day, break into hours
    titleFn: (startDate: Date, numDays: number) =>
      `Hourly activity on ${format(startDate, "d LLL")}`,
    groupingFn: (startDate: Date, numDays: number, rawData: any[]) => {
      const groups = Array.from(Array(24).keys()); // 24 hrs
      const data = groups.map((hr) => {
        return {
          name: `${hr.toString().padStart(2, "0")}00`,
          value: _.countBy(rawData, (d) => d.time?.hrs === hr).true || 0,
        };
      });
      return data;
    },
  },
  {
    testNumDays: (days: number) => days < 4, // if less than 5 days, break into 4 hour groups
    titleFn: (startDate: Date, numDays: number) =>
      `4 hourly activity from ${format(startDate, "d LLL")} - ${format(
        addDays(startDate, numDays - 1),
        "d LLL"
      )}`,
    groupingFn: (startDate: Date, numDays: number, rawData: any[]) => {
      const groups = Array.from(Array(numDays * 6).keys());
      const data = groups.map((groupNum) => {
        const hr = groupNum * 4;
        const start = addHours(startDate, hr);
        const name = format(start, hr % 24 === 0 ? "d LLL" : "HH00");
        const interval = {
          start,
          end: addHours(start, 4),
        };
        return {
          name,
          value:
            _.countBy(rawData, (d) => isWithinInterval(d.time?.date, interval))
              .true || 0,
        };
      });
      return data;
    },
  },
  {
    testNumDays: (days: number) => days <= 30, // if less than 30 days break into days
    titleFn: (startDate: Date, numDays: number) =>
      `Daily activity from ${format(startDate, "d LLL")} - ${format(
        addDays(startDate, numDays - 1),
        "d LLL"
      )}`,
    groupingFn: (startDate: Date, numDays: number, rawData: any[]) => {
      const groups = Array.from(Array(numDays).keys());
      const data = groups.map((groupNum) => {
        const start = addDays(startDate, groupNum);
        const name = format(start, "d LLL");
        const interval = {
          start,
          end: addDays(start, 1),
        };
        return {
          name,
          value:
            _.countBy(rawData, (d) => {
              return isWithinInterval(d.time?.date, interval);
            }).true || 0,
        };
      });
      return data;
    },
  },
  {
    testNumDays: (days: number) => true, // lastly, break into weeks
    titleFn: (startDate: Date, numDays: number) =>
      `Weekly activity from ${format(startDate, "d LLL")} - ${format(
        addDays(startDate, numDays - 1),
        "d LLL"
      )}`,
    groupingFn: (startDate: Date, numDays: number, rawData: any[]) => {
      const numWeeks = Math.ceil(numDays / 7);
      const groups = Array.from(Array(numWeeks).keys());
      const data = groups.map((groupNum) => {
        const start = addWeeks(startDate, groupNum);
        const end = addDays(start, 6);
        const name = `${format(start, "d LLL")} - ${format(end, "d LLL")}`;
        const interval = {
          start,
          end,
        };
        return {
          name,
          value:
            _.countBy(rawData, (d) => isWithinInterval(d.time?.date, interval))
              .true || 0,
        };
      });
      return data;
    },
  },
];

const TimelineChart = ({
  displayData,
  days,
  dateRange,
}: {
  displayData: any[];
  days: number;
  dateRange: IDateRange;
}) => {
  const { titleFn, groupingFn } =
    configOptions.find((o) => o.testNumDays(Math.abs(days))) ||
    configOptions[2];
  const data = groupingFn(dateRange.startDate, days, displayData);

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-white border px-2 py-1">
          <p className="font-bold text-sm">{`${payload[0].value}`}</p>
          <p className="text-xs">{`${label}`}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div className="space-y-4 py-4">
      <div className="space-y-10 py-4">
        <div className="text-sm font-bold">
          {titleFn(dateRange.startDate, days)}
        </div>
        <div className="min-w-[540px] min-h-[320px] w-full h-fit">
          <ResponsiveContainer width={"100%"} height={320}>
            <BarChart data={data}>
              <XAxis dataKey="name" fontSize={"0.75rem"} />
              <YAxis fontSize={"0.75rem"} />
              <Tooltip content={CustomTooltip} />
              <Bar dataKey="value" fill={hlColor} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export { TimelineChart };
